/**
 * @fileOverview This file contains the implementation of the Directory component used in the questionnaire module.
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

import React from 'react'
import "../../scss/Components/Questionnaire/_directory.scss"
import { IconButton, Skeleton, Typography } from '@mui/material'
import { TreeView, TreeItem } from '@mui/x-tree-view'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/sharp-regular-svg-icons';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

/**
 * Section directory component of a questionnaire
 * @param {Function} {manageReturnToAllQ} - Function to handle the return to questionnaire selection
 * @param {Function} {setSelectedSection} - Function to change state of selectedSection
 * @param {State} {selectedSection} - State of selected section of a questionnaire
 * @param {Function} {setSectionName} - Function to change state of sectionName
 * @param {State} {directoryData} - State of directory data (sections of a selected questionnaire)
 * @param {State} {isDirectoryLoading} - State to handle the directory display 
 * @returns {Component} Directory component
 */
const Directory = ({ manageReturnToAllQ, setSelectedSection, selectedSection, setSectionName, directoryData, isDirectoryLoading }) => {
    const questionnaireName = localStorage.getItem("qNameForShow");

    /**
     * Function to handle section selection on directory
     * @param {number} section - Data of selected section
     */
    const handleSectionSelect = (section) => {
      setSelectedSection(section.id);
      setSectionName(section.name);
    };

    /**
     * Recursively traverses a tree structure and returns an array with all nodeIds
     * @param {Object} nodes - The array of nodes to traverse
     * @returns {string[]} An array with all nodeIds
     */
    const getAllNodeIds = (nodes) => {
      let ids = [];
      nodes.forEach((node) => {
        ids.push(String(node.id));
        if (Array.isArray(node.subSections)) {
          ids = ids.concat(getAllNodeIds(node.subSections));
        }
      });
      return ids;
    };

    /**
     * Recursively renders the tree nodes
     * @param {Object} nodes - The tree node data
     * @returns {Component} The rendered tree node
     */
    const renderTree = (nodes) => (
      <TreeItem
        key={nodes.id}
        nodeId={String(nodes.id)}
        label={
          <div className="tree-item-label">
            <Typography className="section-label">{nodes.name}</Typography>
              {nodes.question_count > 0 && (
                <Typography variant="body2" className="questions-sublabel">
                  {nodes.question_count} Preguntas
                </Typography>
              )}
          </div>
        }
        onClick={() => handleSectionSelect(nodes)}
      >
        {Array.isArray(nodes.subSections) 
          ? nodes.subSections.map((node) => renderTree(node)) 
          : null
        }
      </TreeItem>
    );

    return (
        <div className='directory-component-container'>
            <div className='questionnaire-title-container'>
                <IconButton size='medium' className='back-icon-container' onClick={manageReturnToAllQ}>
                    <FontAwesomeIcon icon={faChevronLeft} className='back-icon'/>
                </IconButton>
                <Typography className='questionnaire-title'>{questionnaireName}</Typography>
            </div>

            {isDirectoryLoading ? (
              <div className='directory-main-content'>
                <Skeleton variant="rounded" className='directory-skeleton' />
              </div>
            ) : (
              <div className='directory-main-content'>
                <TreeView
                  aria-label="section directory navigator"
                  defaultCollapseIcon={<FontAwesomeIcon icon={faChevronDown}/>}
                  defaultExpandIcon={<FontAwesomeIcon icon={faChevronRight}/>}
                  className='directory-tree-view'
                  defaultSelected={String(selectedSection)}
                  defaultExpanded={getAllNodeIds(directoryData)}
                >
                  {directoryData.map((section) => renderTree(section))}
                </TreeView>
              </div>
            )}
        </div>
    )
}

export default Directory